import { yupResolver } from "@hookform/resolvers/yup"
import { useEffect, useMemo, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import * as yup from "yup"
import { ILoyaltyLevel } from "@/shared/api/loyalty-levels"
import { Modal } from "@/shared/ui/modal"
import { LoyaltyLevelFormValues, LoyaltyLevelModalProps } from "../model/types"

const acceptedMimeTypes = ["image/png", "image/jpeg", "image/webp"]
const acceptedExtensions = [".png", ".jpg", ".jpeg", ".webp"]
const maxImageSize = 8 * 1024 * 1024

const emptyValues: LoyaltyLevelFormValues = {
    level: "",
    name: "",
    cashback_percent: "",
    paid_amount_rub: "",
    icon: null,
}

const getIconUrl = (level: ILoyaltyLevel | null): string => level?.icon_file_url ?? ""

const isValidIconType = (file: File): boolean => {
    const extension = `.${file.name.split(".").pop()?.toLowerCase() ?? ""}`
    return acceptedExtensions.includes(extension) || acceptedMimeTypes.includes(file.type)
}

const buildSchema = (levels: ILoyaltyLevel[], currentLevel: ILoyaltyLevel | null) => {
    const isEditing = Boolean(currentLevel)

    return yup.object({
        level: yup
            .number()
            .transform((value, originalValue) => originalValue === "" ? undefined : value)
            .typeError("Введите номер уровня")
            .integer("Уровень должен быть целым числом")
            .positive("Уровень должен быть больше 0")
            .required("Введите номер уровня")
            .test("unique-level", "Уровень с таким номером уже существует", (value) => {
                if (typeof value !== "number") {
                    return true
                }

                if (currentLevel?.level === value) {
                    return true
                }

                return !levels.some((level) => level.level === value)
            }),
        name: yup
            .string()
            .trim()
            .required("Введите название уровня"),
        cashback_percent: yup
            .number()
            .transform((value, originalValue) => originalValue === "" ? undefined : value)
            .typeError("Укажите число")
            .min(0, "Кэшбэк не может быть меньше 0%")
            .max(100, "Кэшбэк не может быть больше 100%")
            .required("Введите кэшбэк"),
        paid_amount_rub: yup
            .number()
            .transform((value, originalValue) => originalValue === "" ? undefined : value)
            .typeError("Укажите число")
            .min(0, "Порог не может быть меньше 0 ₽")
            .required("Введите порог"),
        icon: yup
            .mixed<FileList>()
            .test("required-icon", "Добавьте иконку уровня", (value) => {
                if (isEditing) {
                    return true
                }

                return Boolean(value?.[0])
            })
            .test("icon-type", "Файл должен быть в формате PNG, JPG, JPEG или WEBP", (value) => {
                const file = value?.[0]
                return !file || isValidIconType(file)
            })
            .test("icon-size", "Размер файла не должен превышать 8 МБ", (value) => {
                const file = value?.[0]
                return !file || file.size <= maxImageSize
            }),
    })
}

const numberInputOptions = {
    setValueAs: (value: string) => value === "" ? "" : Number(value),
}

export const LoyaltyLevelModal = ({
    isOpen,
    currentLevel,
    levels,
    isSubmitting,
    onClose,
    onSubmit,
}: LoyaltyLevelModalProps) => {
    const isEditing = Boolean(currentLevel)
    const [iconPreview, setIconPreview] = useState("")
    const schema = useMemo(() => buildSchema(levels, currentLevel), [levels, currentLevel])

    const {
        register,
        handleSubmit,
        reset,
        watch,
        formState: { errors },
    } = useForm<LoyaltyLevelFormValues>({
        defaultValues: emptyValues,
        resolver: yupResolver(schema) as any,
    })

    const iconFiles = watch("icon")

    useEffect(() => {
        if (!isOpen) {
            reset(emptyValues)
            setIconPreview("")
            return
        }

        reset(currentLevel
            ? {
                level: currentLevel.level,
                name: currentLevel.name,
                cashback_percent: currentLevel.cashback_percent,
                paid_amount_rub: currentLevel.paid_amount_rub,
                icon: null,
            }
            : emptyValues
        )
        setIconPreview(getIconUrl(currentLevel))
    }, [currentLevel, isOpen, reset])

    useEffect(() => {
        const file = iconFiles?.[0]
        if (!file || !isValidIconType(file) || file.size > maxImageSize) {
            setIconPreview(getIconUrl(currentLevel))
            return
        }

        const previewUrl = URL.createObjectURL(file)
        setIconPreview(previewUrl)

        return () => URL.revokeObjectURL(previewUrl)
    }, [currentLevel, iconFiles])

    const submit: SubmitHandler<LoyaltyLevelFormValues> = async (values) => {
        await onSubmit(values)
    }

    return (
        <Modal
            isOpen={isOpen}
            title={isEditing ? "Редактировать уровень" : "Создать уровень"}
            onRequestClose={onClose}
        >
            <form onSubmit={handleSubmit(submit)} className="flex flex-col gap-4 min-w-[560px]">
                <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors.level && "border-color-danger"}`}>
                    <div className="flex justify-between w-full items-center">
                        <label className="font-semibold uppercase text-base text-[#828282]">Уровень</label>
                        {errors.level && <label className="text-base text-color-danger">{errors.level.message}</label>}
                    </div>
                    <input
                        type="number"
                        min={1}
                        readOnly={isEditing}
                        placeholder="Введите номер уровня"
                        className="w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark read-only:text-color-dark/70 read-only:bg-transparent"
                        {...register("level", numberInputOptions)}
                    />
                </div>

                <div className={`flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors.name && "border-color-danger"}`}>
                    <div className="flex justify-between w-full items-center">
                        <label className="font-semibold uppercase text-base text-[#828282]">Название</label>
                        {errors.name && <label className="text-base text-color-danger">{errors.name.message}</label>}
                    </div>
                    <input
                        placeholder="Введите название"
                        className="w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark"
                        {...register("name")}
                    />
                </div>

                <div className="grid grid-cols-12 gap-3">
                    <div className={`col-span-6 flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors.cashback_percent && "border-color-danger"}`}>
                        <div className="flex justify-between w-full items-center">
                            <label className="font-semibold uppercase text-base text-[#828282]">Кэшбэк %</label>
                            {errors.cashback_percent && (
                                <label className="text-base text-color-danger">{errors.cashback_percent.message}</label>
                            )}
                        </div>
                        <input
                            type="number"
                            min={0}
                            max={100}
                            step="0.01"
                            placeholder="0"
                            className="w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark"
                            {...register("cashback_percent", numberInputOptions)}
                        />
                    </div>
                    <div className={`col-span-6 flex flex-col gap-[6px] pb-[8px] border-b-[1.5px] border-[#B9C9C6] ${errors.paid_amount_rub && "border-color-danger"}`}>
                        <div className="flex justify-between w-full items-center">
                            <label className="font-semibold uppercase text-base text-[#828282]">Порог ₽</label>
                            {errors.paid_amount_rub && (
                                <label className="text-base text-color-danger">{errors.paid_amount_rub.message}</label>
                            )}
                        </div>
                        <input
                            type="number"
                            min={0}
                            step="0.01"
                            placeholder="0"
                            className="w-full outline-0 text-lg [&::placeholder]:text-color-dark/70 [&::placeholder]:text-base text-color-dark"
                            {...register("paid_amount_rub", numberInputOptions)}
                        />
                    </div>
                </div>

                <div className="flex flex-col gap-2">
                    <div className="flex justify-between w-full items-center">
                        <label className="font-semibold uppercase text-base text-[#828282]">Иконка</label>
                        {errors.icon && <label className="text-base text-color-danger">{errors.icon.message}</label>}
                    </div>
                    <input type="file" accept=".png,.jpg,.jpeg,.webp" {...register("icon")} />
                    {Boolean(iconPreview) && (
                        <div className="w-full h-[120px] rounded-xl overflow-hidden bg-[#F7F8FA]">
                            <img src={iconPreview} alt="" className="w-full h-full object-contain" />
                        </div>
                    )}
                    <div className="text-sm text-[#777777]">
                        Поддерживаются PNG, JPG, JPEG, WEBP. Максимум 8 МБ.
                    </div>
                </div>

                <div className="w-full flex gap-3">
                    <button onClick={onClose} type="button" className="form_cancel_button">
                        Отменить
                    </button>
                    <button disabled={isSubmitting} className="form_handle_button" type="submit">
                        {isEditing ? "Сохранить" : "Создать"}
                    </button>
                </div>
            </form>
        </Modal>
    )
}
